import { useState, type FC } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Layout,
  Menu,
  Button,
  Drawer,
  Space,
  type MenuProps,
} from 'antd';
import {
  MenuOutlined,
  SunOutlined,
  MoonOutlined,
} from '@ant-design/icons';
import { useSiteContent } from '@/context/ContentProvider';
import { trackNavigation } from '@/utils/analytics';
import { SearchInput } from './SearchInput';

const { Header } = Layout;

interface NavbarProps {
  isDark: boolean;
  onToggleTheme: () => void;
}

export const Navbar: FC<NavbarProps> = ({ isDark, onToggleTheme }) => {
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const siteContent = useSiteContent();
  const { navbar } = siteContent;

  // When on a project detail page, anchor the active tab to where the user
  // arrived from (Home vs Projects list), not the current pathname. This
  // prevents the navbar from "jumping" to Projects when the user clicked a
  // featured project on Home.
  const fromPath =
    (location.state as { from?: string } | null)?.from ?? null;
  const onProjectDetail = /^\/portfolio\/[^/]+/.test(location.pathname);
  const matchPath = onProjectDetail && fromPath ? fromPath : location.pathname;

  const activeKey =
    navbar.items.find((item) => {
      if (item.key === '/') return matchPath === '/' || matchPath.startsWith('/?');
      return matchPath.startsWith(item.key);
    })?.key || '/';

  const handleMenuClick: MenuProps['onClick'] = (e) => {
    trackNavigation(e.key);
    setDrawerOpen(false);
  };

  const menuItems: MenuProps['items'] = navbar.items.map((item) => ({
    key: item.key,
    label: <Link to={item.key}>{item.label}</Link>,
  }));

  return (
    // Outer shell: full-width fixed strip, transparent — its only job is
    // to center the floating pill and keep view-transition naming stable.
    // Pointer events are disabled here so clicks outside the pill don't
    // get swallowed by the strip.
    <Header
      className="navbar-blur navbar-shell"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        width: '100%',
        zIndex: 1000,
        background: 'transparent',
        border: 'none',
        padding: '14px 16px 0',
        height: 'auto',
        lineHeight: 'normal',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <div className="navbar-pill">
        <Link to="/" className="navbar-pill__brand">
          <span className="brand-full">{navbar.brandFull || navbar.brand}</span>
          <span className="brand-short">{navbar.brand}</span>
          <span style={{ color: 'var(--color-accent)' }}>.</span>
        </Link>

        {/* Desktop nav — centered inline items inside the pill */}
        <Menu
          mode="horizontal"
          selectedKeys={[activeKey]}
          items={menuItems}
          onClick={handleMenuClick}
          className="desktop-nav navbar-pill__menu"
          disabledOverflow
        />

        <Space size={6} className="navbar-pill__actions" style={{ flexShrink: 0 }}>
          <SearchInput />
          <Button
            type="text"
            shape="circle"
            icon={isDark ? <SunOutlined /> : <MoonOutlined />}
            onClick={onToggleTheme}
            aria-label="Toggle theme"
          />
          <Button
            type="text"
            shape="circle"
            icon={<MenuOutlined />}
            onClick={() => setDrawerOpen(true)}
            className="mobile-menu-btn"
            aria-label="Open menu"
          />
        </Space>
      </div>

      <Drawer
        title={navbar.drawerTitle}
        placement="right"
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
        styles={{ wrapper: { width: 280 } }}
      >
        <Menu
          mode="vertical"
          selectedKeys={[activeKey]}
          items={menuItems}
          onClick={handleMenuClick}
          style={{ border: 'none' }}
        />
      </Drawer>

      <style>{`
        .desktop-nav {
          display: flex !important;
        }
        .mobile-menu-btn {
          display: none !important;
        }
        .brand-short {
          display: none;
        }
        .brand-full {
          display: inline;
        }
        /* 7 inline items + brand + search + 2 buttons need ~1200px to
           sit inside a pill without overlapping. Collapse to the drawer
           early enough that menu items never crash into the search box. */
        @media (max-width: 1180px) {
          .desktop-nav {
            display: none !important;
          }
          .mobile-menu-btn {
            display: inline-flex !important;
          }
        }
        @media (max-width: 600px) {
          .brand-full {
            display: none;
          }
          .brand-short {
            display: inline;
          }
        }
      `}</style>
    </Header>
  );
};
